import { DomainDto } from '../../../dtos/domain'
import { JourneyBuilderMode } from '../enums/journey-builder-mode.enum'
import * as React from 'react'
import { Dispatch } from 'react'
import { Journey } from './journey-api-response'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { ActionStep, DelayStep, JourneyEdge, JourneyNode, JourneyStep } from './journey-nodes'

// region JourneyContext

export type JourneyProps = {
    domain: DomainDto
    journey: Journey
    mode: JourneyBuilderMode
    callbacks: JourneyStateCallbacks
    nodes: JourneyNode[]
    edges: JourneyEdge[]
    copy: CopyState
    editorOpen: boolean
}

export enum CopyStates {
    CLEAR = 'clear',
    FILLED = 'filled',
}

export enum CopyTypes {
    COPY,
    CUT,
}

type CopyState =
    | {
          state: CopyStates.CLEAR
          node: null
      }
    | {
          state: CopyStates.FILLED
          type: CopyTypes.CUT | CopyTypes.COPY
          node: JourneyNode<ActionStep | DelayStep>
      }

type JourneyStateCallbacks = {
    onNodeAdd: <T extends JourneyStep>(node: JourneyNode<T>) => void
    handleNodeRemove: <T extends JourneyStep>(node: JourneyNode<T>) => void
    onNodeClick: <T extends JourneyStep>(ev: React.MouseEvent<Element, MouseEvent>, node: JourneyNode<T>) => void
    onNodeUpdate: <T extends JourneyStep>(node: JourneyNode<T>, updates: JourneyNode<T>) => JourneyNode[]
    emitChanges?: (journey: JourneyState) => void
}

export type JourneyState = {
    domain: DomainDto
    journey?: Journey
    mode: JourneyBuilderMode
    callbacks: JourneyStateCallbacks
    nodes: JourneyNode[]
    activeNode?: JourneyNode
    edges: JourneyEdge[]
    dependencies: {
        integrations?: Map<number, GetDomainIntegrationResponse>
    }
    copy: CopyState
    editorOpen: boolean
    editorFooter?: React.ReactNode
    closePopoversSignal: number
}

export type ActionDispatcher = (action: JourneyDispatchActionPack) => void
export type JourneyReducerElements = [context: JourneyState, dispatch: Dispatch<JourneyDispatchActionPack>]

export enum JourneyAction {
    SET_JOURNEY = 'setJourney',
    SET_ORIGINAL_STEPS = 'setOriginalSteps',
    UPDATE_ORIGINAL_STEPS = 'updateOriginalSteps',
    SET_ACTIVE_NODE = 'setActiveNode',
    ON_NODE_ADD = 'onNodeAdd',
    ON_NODE_UPDATE = 'onNodeUpdate',
    ON_NODE_REMOVE = 'onNodeRemove',
    ON_JOURNEY_CHANGES = 'onJourneyChanges',
    SET_JOURNEY_DEPENDENCIES = 'setJourneyDependencies',
    SET_JOURNEY_INTEGRATIONS = 'setJourneyIntegrationsDependencies',
    SET_COPY_STATE = 'setCopyState',
    SET_EDITOR_OPEN = 'setEditorOpen',
    SET_EDITOR_FOOTER = 'setEditorFooter',
    CLOSE_ALL_POPOVERS = 'closeAllPopovers',
}

export type JourneySetPack = {
    action: JourneyAction.SET_JOURNEY
    data: Journey
}

export type JourneyStepsSetPack = {
    action: JourneyAction.SET_ORIGINAL_STEPS
    data: JourneyStep[]
}

export type JourneyStepsUpdatePack = {
    action: JourneyAction.UPDATE_ORIGINAL_STEPS
    data: JourneyStep[]
}

export type JourneySetActiveNodePack = {
    action: JourneyAction.SET_ACTIVE_NODE
    data: JourneyNode | null
}

export type JourneyNodeAddPack = {
    action: JourneyAction.ON_NODE_ADD
    data: JourneyNode
}

export type JourneyNodeUpdatePack<T extends JourneyStep = JourneyStep> = {
    action: JourneyAction.ON_NODE_UPDATE
    data: { updates: JourneyNode<T> }
}

export type JourneyNodeRemovePack = {
    action: JourneyAction.ON_NODE_REMOVE
    data: JourneyNode
}

export type JourneyChangesPack = {
    action: JourneyAction.ON_JOURNEY_CHANGES
    data: { nodes?: JourneyNode[]; edges?: JourneyEdge[] }
}

export type JourneySetDependenciesPack = {
    action: JourneyAction.SET_JOURNEY_DEPENDENCIES
    data: JourneyState['dependencies']
}

export type JourneySetIntegrationsDependenciesPack = {
    action: JourneyAction.SET_JOURNEY_INTEGRATIONS
    data: JourneyState['dependencies']['integrations']
}

export type JourneyUpdateCopyPack = {
    action: JourneyAction.SET_COPY_STATE
    data: { type: CopyTypes; node: JourneyNode<ActionStep | DelayStep> } | { node: null }
}

export type JourneySetEditorOpenPack = {
    action: JourneyAction.SET_EDITOR_OPEN
    data: boolean
}

export type JourneySetEditorFooterPack = {
    action: JourneyAction.SET_EDITOR_FOOTER
    data?: React.ReactNode
}

export type JourneyCloseAllPopoversPack = {
    action: JourneyAction.CLOSE_ALL_POPOVERS
}

export type JourneyDispatchActionPack =
    | JourneySetPack
    | JourneySetActiveNodePack
    | JourneyNodeAddPack
    | JourneyNodeRemovePack
    | JourneyNodeUpdatePack
    | JourneyChangesPack
    | JourneyStepsSetPack
    | JourneyStepsUpdatePack
    | JourneySetDependenciesPack
    | JourneySetIntegrationsDependenciesPack
    | JourneyUpdateCopyPack
    | JourneySetEditorOpenPack
    | JourneySetEditorFooterPack
    | JourneyCloseAllPopoversPack

// endregion

// end region
